import {
  inject,
  signal,
  computed,
  Component,
  ViewChild,
  DestroyRef,
  AfterViewChecked,
  ViewEncapsulation,
  NgZone,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SuiInputComponent } from '@sui/input';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { GridStackNode, GridStackOptions } from 'gridstack';
import { TranslateService } from '@ngx-translate/core';
import { GridstackComponent } from 'gridstack/dist/angular';
import {
  faEye,
  faSpinner,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';

import { UtilsService } from '../../services/utils.service';
import { SidebarService } from '../../services/sidebar.service';
import { WidgetViewProperties } from 'src/app/api/services/feed-api.service';
import { PresentationService } from '../../services/presentation.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { ObjectActionsService } from '../../services/object-actions.service';
import { GridsterConfigService } from '../../services/gridster-config.service';
import { DashboardAutoRefreshManagerService } from '../../services/dashboard-auto-refresh-manager.service';
import { PanelComponentMappingService } from '../../services/panel-component-mapping.service';
import { DashboardPageCoordinatorService } from '../../services/dashboard-page-coordinator.service';
import { DashboardPagePersistenceService } from '../../services/dashboard-page-persistence.service';

import {
  SidebarButtonConfig,
  SidebarButtonBehavior,
} from '../../components/sidebar/sidebar.component';
import { ShareMode } from '../../components/popups/share-dialog/share-dialog.component';

import {
  Widget,
  Dashboard,
  DashboardPage,
  ObjectPermission,
} from 'src/app/core/models/core.models';
import { UpdateDashboardPageParams } from '../../models/widget.models';
import { WidgetType, NomObjectClass } from '../../constants';
import { AppFeatures } from '../../app.features';

import { activateFilters } from '../../actions/feed.actions';
import {
  selectObjects,
  clearSelectedObjects,
} from '../../actions/selected.actons';
import { objectChanged } from '../../actions/core.actions';
import * as widgetActions from '../../actions/widget.actions';
import {
  selectDashboardPage,
  deleteDashboardPageSuccess,
} from '../../actions/object.actions';

import {
  selectCurrentObject,
  selectDashboardPages,
  selectSelectedPageId,
  selectIsLoadingWidgets,
  selectObjectIsExporting,
  selectCurrentObjectEditMode,
  selectSelectedPlaceholderId,
  selectWidgetSearchString,
  selectWidgetsLanguageId,
  selectObjectWidgetViewProperties,
  selectExportSchedulesCount,
} from '../../reducers';
import { AppState } from 'src/app/reducers';
import {
  ObjectWidgetsScope,
  injectObjectWidgetsQuery,
} from 'src/app/api/queries/widget.queries';
import {
  applyStagedLayout,
  filterWidgetsBySearchString,
  sortWidgetsByPosition,
} from 'src/app/shared/utils/widget-list.utils';
import { CurrentViewWidgetsService } from '../../services/current-view-widgets.service';
import { WidgetLayoutStagingService } from '../../services/widget-layout-staging.service';
import { RecentObjectsService } from '../../services/recent-objects.service';
import { WidgetPersistenceService } from '../../services/widget-persistence.service';
import { WidgetViewStateService } from '../../services/widget-view-state.service';
import { WidgetViewPresetService } from '../../services/widget-view-preset.service';

import {
  tap,
  map,
  take,
  pluck,
  filter,
  interval,
  switchMap,
  Observable,
  debounceTime,
  BehaviorSubject,
  combineLatest,
} from 'rxjs';
import { UserFacadeService } from '@services/user-facade.service';
import { produce } from 'immer';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DashboardAutoRefreshManagerService],
})
export class DashboardComponent implements AfterViewChecked, OnDestroy {
  private _gridstack: GridstackComponent;
  @ViewChild(GridstackComponent)
  set gridstack(value: GridstackComponent) {
    // ! we have this "setter" approach because using it in the ngAfterViewInit - this.gridstack is undefined (waiting widgets to come)
    this._gridstack = value;
    if (value) {
      this.setupGridstackListeners();
    }
  }
  get gridstack(): GridstackComponent {
    return this._gridstack;
  }

  private readonly layoutStaging = inject(WidgetLayoutStagingService);
  private readonly widgetPersistence = inject(WidgetPersistenceService);
  private readonly currentViewWidgets = inject(CurrentViewWidgetsService);
  private readonly recentObjects = inject(RecentObjectsService);
  private readonly widgetViewState = inject(WidgetViewStateService);
  private readonly widgetViewPreset = inject(WidgetViewPresetService);

  // ---- the selected page's widget list ----------------------------------------
  // * `pageId` is always a real page here: the query stays disabled until one is
  // * selected, which is what keeps this key away from Presentation Mode's
  // * (`pageId: null`) - see WAP-874.
  private readonly widgetsScope = signal<ObjectWidgetsScope | null>(null);
  private readonly widgetSearchQuery = signal('');
  private readonly widgetsLanguageId = signal<number | null>(null);

  readonly widgetsQuery = injectObjectWidgetsQuery(this.widgetsScope);

  private readonly widgetList = computed(() =>
    filterWidgetsBySearchString(
      sortWidgetsByPosition(
        applyStagedLayout(
          this.widgetsQuery.data() ?? [],
          this.layoutStaging.layout(),
        ),
      ),
      this.widgetSearchQuery(),
      this.widgetViewPreset.editMode(),
      this.widgetsLanguageId(),
    ),
  );

  private pendingScrollWidgetId: string | null = null;
  selectSelectedPlaceholderId$: Observable<string>;
  currentObjectEditMode$: Observable<boolean>;
  isDownloading$: Observable<boolean>;
  objectViewProperties$: Observable<WidgetViewProperties>;
  widgetsLoading$: Observable<boolean>;

  // * track which Widget is currently being resized
  resizingWidgetId$ = new BehaviorSubject<string | null>(null);
  // * track if any widget is currently being dragged
  isDragging$ = new BehaviorSubject<boolean>(false);

  // * Multi-page dashboard support - NgRx state-driven properties
  dashboardPages$: Observable<DashboardPage[]>;
  selectedPageId$: Observable<number>;
  currentPageWidgets$: Observable<
    Array<{ widget: Widget; gridOptions: GridStackNode }>
  >;
  selectedPage$: Observable<DashboardPage | null>;
  private gridOptionsCache = new Map<string, GridStackNode>();

  // * Presentation mode state
  showPresentationMode = false;
  presentationSinglePageId: number | null = null;

  // * Edit state for Page Title and Description
  editingTitle = false;
  editingDescription = false;
  editTitleValue = '';
  editDescriptionValue = '';
  private titleFocused = false;
  private descriptionFocused = false;

  /**
   * Gates the "no widgets on this page" message so it can't show before the first
   * list response, or while switching pages.
   */
  get loadWidgetPassed(): boolean {
    return this.widgetsQuery.isSuccess();
  }

  @ViewChild('titleInput') titleInput?: SuiInputComponent;
  @ViewChild('descriptionInput')
  descriptionInput?: SuiInputComponent;

  gridsterConfig: GridStackOptions;
  dashboard: Dashboard;
  dashboardId: string;
  sidebarButtons: SidebarButtonConfig[];
  exportSchedulesCount: number;

  faEye = faEye;
  faSpinner = faSpinner;
  faPenToSquare = faPenToSquare;

  private destroyRef = inject(DestroyRef);
  protected userFacade = inject(UserFacadeService);
  private dashboardAutoReloadManager = inject(DashboardAutoRefreshManagerService);

  autoReloadSecondsLeft$ = this.dashboardAutoReloadManager.secondsLeft$;
  cacheEnabled$ = this.dashboardAutoReloadManager.cacheEnabled$;

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private sidebarPanelService: PanelComponentMappingService,
    public objectActionService: ObjectActionsService,
    private translateService: TranslateService,
    private utilsService: UtilsService,
    private sidebarService: SidebarService,
    private gridsterConfigService: GridsterConfigService,
    private objectEditorService: ObjectEditorService,
    private presentationService: PresentationService,
    private pageCoordinator: DashboardPageCoordinatorService,
    private pagePersistence: DashboardPagePersistenceService,
  ) {
    this.gridsterConfig = this.gridsterConfigService.getDashboardConfig();
    this.objectViewProperties$ = this.store.select(
      selectObjectWidgetViewProperties,
    );

    // * NgRx selectors for multi-page support
    this.dashboardPages$ = this.store.select(selectDashboardPages);
    this.selectedPageId$ = this.store.select(selectSelectedPageId);
    // * the list query is keyed per page, so its data *is* the current page's widgets.
    // * Grid options are pre-built and reference-stable: the gridstack-item
    // * [options] setter calls grid.update() whenever the input reference changes,
    // * so an inline template literal would re-layout every widget on every
    // * change-detection cycle (mirrors feed.component).
    this.currentPageWidgets$ = toObservable(this.widgetList).pipe(
      map((widgets) => {
        const cache = new Map<string, GridStackNode>();
        const items = widgets.map((widget) => {
          const gridOptions = this.toGridOptions(widget);
          cache.set(widget.id, gridOptions);
          return { widget, gridOptions };
        });
        this.gridOptionsCache = cache;
        return items;
      }),
    );

    // * the selected page drives the query key, so selecting a page (or landing on
    // * the replacement page after a deletion) refetches on its own
    combineLatest([
      this.store.select(selectCurrentObject),
      this.store.select(selectSelectedPageId),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([object, pageId]) => {
        this.widgetsScope.set(
          object?.id
            ? {
                objectId: object.id,
                objectClassId: object.object_class_id,
                pageId: pageId ?? null,
              }
            : null,
        );
      });

    combineLatest([
      this.store.select(selectWidgetSearchString),
      this.store.select(selectWidgetsLanguageId),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([searchString, languageId]) => {
        this.widgetSearchQuery.set(searchString ?? '');
        this.widgetsLanguageId.set(languageId);
      });

    // * Computed observable for selected page details
    this.selectedPage$ = this.store.select(selectDashboardPages).pipe(
      switchMap((pages) =>
        this.store.select(selectSelectedPageId).pipe(
          map(
            (selectedId) =>
              pages.find((page) => page.id === selectedId) || null,
          ),
          map((page) => {
            return {
              ...page,
              description: page.description || 'No Description',
            };
          }),
        ),
      ),
    );

    this.selectSelectedPlaceholderId$ = this.store.select(
      selectSelectedPlaceholderId,
    );

    this.currentObjectEditMode$ = this.store.select(
      selectCurrentObjectEditMode,
    );

    this.isDownloading$ = this.store.select(selectObjectIsExporting);
    // * the NgRx flag covers a bulk delete; the list's own first load comes from
    // * the query
    this.widgetsLoading$ = combineLatest([
      this.store.select(selectIsLoadingWidgets),
      toObservable(computed(() => this.widgetsQuery.isLoading())),
    ]).pipe(map(([deleting, loadingList]) => deleting || loadingList));

    // * Listen for presentation mode actions
    this.setupPresentationModeListener();
  }

  public ngOnInit(): void {
    this.route.data
      .pipe(
        // 1) pull dashboard straight out of the data object
        pluck('dashboard'),

        // 2) only continue if there actually is a dashboard
        filter((dashboard): dashboard is Dashboard => !!dashboard),

        // 3a) local side-effects
        tap((dashboard) => {
          this.dashboard = dashboard;
          this.dashboardId = dashboard.id;

          this.setupSidebar(dashboard);
        }),

        // 3b) store side-effects
        tap((dashboard) => {
          this.store.dispatch(objectChanged({ object: dashboard }));
          this.recentObjects.record(dashboard);

          // * initial Page Selection
          this.pageCoordinator
            .initializeDashboard(dashboard, this.route)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }),

        // 4) auto-unsubscribe when the component is destroyed
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * listen for Page Deletion results
    this.setupPageDeletionListener();

    // * listen for widget creation success to scroll to new widget
    this.setupWidgetCreationListener();

    this.startDasbhoardSubscribtion();

    this.schedulesCountSubscription();

    // * Start auto-reload if enabled
    this.dashboardAutoReloadManager.startAutoRefresh();
  }

  private setupPageDeletionListener(): void {
    this.actions$
      .pipe(
        ofType(deleteDashboardPageSuccess),
        tap((action) => {
          this.store
            .select(selectCurrentObject)
            .pipe(
              take(1),
              filter(
                (currentObject): currentObject is Dashboard =>
                  !!currentObject &&
                  currentObject.object_class_id === NomObjectClass.DASHBOARD,
              ),
              tap((dashboard) => {
                this.dashboard = dashboard;
                this.pageCoordinator.handlePageDeletion(
                  action.deletedPageId,
                  this.route,
                );
              }),
            )
            .subscribe();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private startDasbhoardSubscribtion() {
    this.store
      .select(selectCurrentObject)
      .pipe(
        filter(
          (currentObject): currentObject is Dashboard =>
            !!currentObject &&
            currentObject.object_class_id === NomObjectClass.DASHBOARD,
        ),
        tap((dashboard) => {
          // Side effect: assign
          this.dashboard = dashboard;
          this.setupSidebar(dashboard);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private schedulesCountSubscription() {
    this.store
      .select(selectExportSchedulesCount)
      .pipe(
        tap((count) => {
          this.exportSchedulesCount = count;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setupGridstackListeners(): void {
    // ! this is the better way to track the Widget that is being resized
    // ! do not try with changeCB resize start/end events - it will not work as expected
    this.gridstack.grid?.on('resizestart', (event, el) => {
      const widgetId = el.gridstackNode?.id;
      if (widgetId) {
        this.resizingWidgetId$.next(widgetId.toString());
      }
    });

    this.gridstack.grid?.on('resizestop', (event, el) => {
      this.resizingWidgetId$.next(null);
    });

    this.gridstack.grid?.on('dragstart', (event, el) => {
      this.isDragging$.next(true);
    });

    this.gridstack.grid?.on('dragstop', (event, el) => {
      this.isDragging$.next(false);
    });
  }

  ngAfterViewInit(): void {
    this.route.queryParams.subscribe((params) => {
      const open = params['open'];
      if (open) {
        if (open == 'export-schedules') {
          // * no prefetch: the schedules panel loads its own list on init
          this.sidebarService.selectComponent(this.sidebarButtons[3], true);
        }
      }
    });
  }

  ngOnDestroy(): void {
    // console.log('--- DashboardComponent destroyed ---');
    this.dashboardAutoReloadManager.stopAutoRefresh();
    this.sidebarService.closeSidebar();
  }

  ngAfterViewChecked(): void {
    // * auto-focus inputs when entering edit mode (only once)
    if (this.editingTitle && this.titleInput && !this.titleFocused) {
      this.titleInput.focus();
      this.titleInput.select();
      this.titleFocused = true;
    }
    if (
      this.editingDescription &&
      this.descriptionInput &&
      !this.descriptionFocused
    ) {
      this.descriptionInput.focus();
      this.descriptionInput.select();
      this.descriptionFocused = true;
    }

    // ! check if we need to scroll to a newly created widget
    if (this.pendingScrollWidgetId) {
      const widgetElement = document.getElementById(
        `widget-${this.pendingScrollWidgetId}`,
      );
      if (widgetElement) {
        this.scrollToWidget(this.pendingScrollWidgetId);
        this.pendingScrollWidgetId = null;
      }
    }
  }

  loadComponent(sidebarButton: SidebarButtonConfig) {
    this.sidebarPanelService.getChildInstance().loadComponent(sidebarButton);
  }

  onGridChange(event: any) {
    if (event && event.nodes && event.nodes.length > 0) {
      this.layoutStaging.stageReposition(
        event.nodes.map((node: any) => ({
          widgetId: node.id,
          x: node.x,
          y: node.y,
          cols: node.w,
          rows: node.h,
        })),
      );
    }
  }

  trackByFn(index: number, item: { widget: Widget }): string {
    return item.widget.id;
  }

  // * Reuses the cached options object when the widget's geometry is unchanged,
  // * so gridstack-item only sees a new input when something actually moved.
  private toGridOptions(widget: Widget): GridStackNode {
    const noResize =
      widget.widget_type_id === WidgetType.WIDGET_TYPE_PLACEHOLDER;
    const cached = this.gridOptionsCache.get(widget.id);
    if (
      cached &&
      cached.x === widget.x &&
      cached.y === widget.y &&
      cached.w === widget.cols &&
      cached.h === widget.rows &&
      cached.noResize === noResize
    ) {
      return cached;
    }
    return {
      x: widget.x,
      y: widget.y,
      w: widget.cols,
      h: widget.rows,
      id: widget.id,
      noResize,
    };
  }

  selectAllWidgets(selected: boolean) {
    if (selected) {
      // * the deep copy keeps the selection slice from holding references into the
      // * widget-list cache entry
      const widgets = this.filterOutPlaceholders(this.widgetList()).map(
        (widget) => this.utilsService.deepCopy(widget),
      );

      this.store.dispatch(selectObjects({ objects: widgets }));
    } else {
      this.store.dispatch(clearSelectedObjects());
    }
  }

  searchWidgets(query: string) {
    if (typeof query === 'string') {
      this.store.dispatch(widgetActions.searchWidgets({ query: query }));
    }
  }

  refreshWidgets() {
    this.store.dispatch(activateFilters());
  }

  private setupSidebar(dashboard: Dashboard) {
    const buttons: SidebarButtonConfig[] = [
      {
        label: this.translateService.instant('sidebar-tools.label.settings'),
        key: 'settings',
        header: this.translateService.instant('sidebar-tools.label.settings'),
        icon: ['fas', 'gear'],
        behaviorType: SidebarButtonBehavior.ACTION,
        actionHandler: () => {
          this.objectEditorService.loadDashboardAndOpenPopup(dashboard.id);
        },
        objectPermission: ['can_edit'],
        permission: AppFeatures.DASHBOARD_SETTINGS_ROOT, // Assuming this permission exists
      },
      {
        label: this.translateService.instant('sidebar-tools.label.widgets'),
        key: 'widgets',
        componentName: 'WidgetSidebarComponent',
        header: this.translateService.instant('sidebar-tools.label.widgets'),
        icon: ['fas', 'chart-bar'],
        objectPermission: ['can_edit'],
        inputs: {
          objectId: dashboard.id,
          objectClassId: NomObjectClass.DASHBOARD,
        },
      },
      // {
      //   label: this.translateService.instant('sidebar-tools.label.filters'),
      //   componentName: 'FiltersComponent',
      //   header: 'Filters',
      //   icon: ['fas', 'filter'],
      // },
      {
        label: this.translateService.instant('sidebar-tools.label.share'),
        componentName: 'ExportComponent',
        key: 'export',
        header: this.translateService.instant('sidebar-tools.label.share'),
        permission: AppFeatures.EXPORT_AND_SHARE_ROOT,
        icon: ['fas', 'share-nodes'],
        objectPermission: ['can_view'],
        inputs: {
          objectId: dashboard.id,
          objectClassId: NomObjectClass.DASHBOARD,
          shareMode: ShareMode.RESULTSET,
        },
      },
      {
        label: this.translateService.instant('sidebar-tools.label.schedules'),
        componentName: 'SchedulesComponent',
        header: this.translateService.instant('sidebar-tools.label.schedules'),
        permission: AppFeatures.SYSTEM_EXPORT_SCHEDULES,
        key: 'schedules',
        icon: ['fas', 'calendar'],
        objectPermission: ['can_view'],
        inputs: {
          objectId: dashboard.id,
          objectClassId: NomObjectClass.DASHBOARD,
        },
        counter: this.exportSchedulesCount,
      },
    ];

    this.sidebarButtons = buttons.filter(
      (b) =>
        this.userFacade.hasAllObjectPermissionsSync(
          dashboard,
          b.objectPermission ?? [],
        ) &&
        (!b.permission ||
          this.userFacade.hasPermissionSync(b.permission) === true),
    );
  }

  private filterOutPlaceholders(widgets: Widget[]): Widget[] {
    return widgets.filter((widget) => widget.widget_type_id !== 10);
  }

  selectPage(pageId: number): void {
    this.pageCoordinator.selectPage(pageId, this.dashboardId, this.route);
  }

  // ! Multi-page dashboard support - add new page method
  createPage(): void {
    this.pagePersistence.createPage(this.dashboardId);
  }

  deletePage(pageId: number): void {
    // * the last-page guard lives in the service, shared with the page navigator
    this.pagePersistence.deletePage(this.dashboardId, pageId);
  }

  reorderPages(event: {
    orderedIds: number[];
    orderedPages: DashboardPage[];
    rollbackPages: DashboardPage[];
  }): void {
    this.pagePersistence.reorderPages({
      dashboardId: this.dashboardId,
      orderedIds: event.orderedIds,
      orderedPages: event.orderedPages,
      rollbackPages: event.rollbackPages,
    });
  }

  onPageVisibilityToggle(event: { pageId: number; visible: boolean }): void {
    this.dashboardPages$.pipe(take(1)).subscribe((pages) => {
      const page = pages.find((p) => p.id === event.pageId);
      if (!page) return;

      this.pagePersistence.updatePage({
        dashboard_id: this.dashboardId,
        page_id: event.pageId,
        is_visible_in_presentation: event.visible,
      });
    });
  }

  // * Presentation mode methods
  enterPresentationMode(): void {
    this.showPresentationMode = true;
    this.dashboardAutoReloadManager.setPresentationMode();
  }

  exitPresentationMode(): void {
    this.dashboardAutoReloadManager.setNormalMode();

    // * refetch this page's list. The dashboard stays mounted behind the overlay,
    // * so its cards are never torn down - they just pick up the new response.
    this.currentViewWidgets.invalidate();

    // * force widget visibility
    this.widgetList().forEach((widget) => {
      this.widgetViewState.setVisible(widget.id, true);
    });

    this.showPresentationMode = false;
    this.presentationSinglePageId = null;
  }

  private setupPresentationModeListener(): void {
    this.presentationService.presentationMode$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((event) => {
          if (event.action === 'show') {
            this.presentationSinglePageId = event.singlePageId ?? null;
            this.enterPresentationMode();
          } else if (event.action === 'hide') {
            this.exitPresentationMode();
          }
        }),
      )
      .subscribe();
  }

  // * Page title and description editing methods
  startEditingTitle(currentTitle: string): void {
    this.editingTitle = true;
    this.editTitleValue = currentTitle;
    // Focus will be handled in template with ViewChild if needed
  }

  startEditingDescription(currentDescription: string): void {
    this.editingDescription = true;
    this.editDescriptionValue = currentDescription || '';
  }

  saveTitle(): void {
    if (this.editTitleValue.trim()) {
      this.selectedPage$
        .pipe(
          take(1),
          filter((page): page is DashboardPage => !!page),
          tap((page) => {
            this.pagePersistence.updatePage({
              dashboard_id: this.dashboardId,
              page_id: page.id,
              name: this.editTitleValue.trim(),
            });
          }),
        )
        .subscribe();
    }

    this.cancelEditTitle();
  }

  saveDescription(): void {
    this.selectedPage$
      .pipe(
        take(1),
        filter((page): page is DashboardPage => !!page),
        tap((page) => {
          this.pagePersistence.updatePage({
            dashboard_id: this.dashboardId,
            page_id: page.id,
            description: this.editDescriptionValue.trim(),
          });
        }),
      )
      .subscribe();
    this.cancelEditDescription();
  }

  cancelEditTitle(): void {
    this.editingTitle = false;
    this.editTitleValue = '';
    this.titleFocused = false;
  }

  cancelEditDescription(): void {
    this.editingDescription = false;
    this.editDescriptionValue = '';
    this.descriptionFocused = false;
  }

  onTitleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveTitle();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditTitle();
    }
  }

  onDescriptionKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveDescription();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditDescription();
    }
  }

  onDragOver(event: DragEvent): void {
    this.ngZone.runOutsideAngular(() => {
      event.preventDefault();
      event.dataTransfer!.dropEffect = 'copy';
    });
  }

  onDrop(event: DragEvent): void {
    this.ngZone.runOutsideAngular(() => {
      event.preventDefault();

      // ! parse JSON outside zone
      const dragData = this.getDragDataOutsideZone(event);

      if (dragData?.type === 'widget-visualization-type') {
        // ! re-enter zone only for state updates
        this.ngZone.run(() => {
          this.createWidgetFromDrop(dragData);
        });
      }
    });
  }

  private createWidgetFromDrop(dragData: any): void {
    this.widgetPersistence.createWidget({
      chartId: dragData.chartId,
      chartTypeId: dragData.chartTypeId,
      objectId: this.dashboard.id,
      objectClassId: NomObjectClass.DASHBOARD,
      dataSourceObjectId: dragData.dataSourceObjectId,
      dataSourceObjectClassId: dragData.dataSourceObjectClassId,
    });
  }

  private setupWidgetCreationListener(): void {
    this.actions$
      .pipe(
        ofType(widgetActions.createWidgetSuccess),
        takeUntilDestroyed(this.destroyRef),
        tap((action) => {
          this.pendingScrollWidgetId = action.widget.id;
        }),
      )
      .subscribe();
  }

  private scrollToWidget(widgetId: string): void {
    const widgetElement = document.getElementById(`widget-${widgetId}`);
    const scrollContainer = document.querySelector('.card-container.widgets');

    if (widgetElement && scrollContainer) {
      // TODO: this is working only with block: 'nearest'
      // TODO: other options like 'center' are pushing the parents up
      // TODO: there are other ways to scroll using getBoundingClientRect and scrollTo
      widgetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  private getDragDataOutsideZone(event: DragEvent): any {
    try {
      const data = event.dataTransfer?.getData('application/json');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      return null;
    }
  }
}
